import React, { useMemo } from 'react';

import { useModal } from '@/features/Modals/hooks/useModal';
import { formatWithCommas, formatWithMaxPrecision } from '@/shared/utils/helpers';
import { useGetRiskLevel } from '@/shared/hooks/useGetRiskLevel';
import { useGetBorrowRate } from '@/shared/hooks/useGetBorrowRate';
import { useFetchPrice } from '@/shared/hooks/useGetPrice';
import { useCalculateCollateralPrices } from '@/shared/hooks/useCalculateCollateralPrices';
import { SkeletonInfo, TooltipWrapper } from '@/shared/ui';
import { cn } from '@/shared/lib/tailwind-merge';

// USD code in hex according to ISO 4217
export const USD_ADDRESS = '0x0000000000000000000000000000000000000348';

const Details = () => {
    const { modalProps } = useModal();

    const {
        riskFactor,
        agreement,
        tokenSymbol,
        assetAmount,
        assetAddress,
        selectedAssets,
        collateralMarketValue,
        collateralStressTested,
        agreementAddress,
    } = modalProps || {};

    const { prices, loading: pricesLoading } = useCalculateCollateralPrices(
        collateralMarketValue,
        agreementAddress,
        agreement,
    );

    const { data: priceData, loading } = useFetchPrice(assetAddress, USD_ADDRESS);

    const { isHigh, isWarning, isLow } = useGetRiskLevel({
        health: riskFactor,
        marginCallThreshold: agreement.marginCallThreshold,
        liquidationThreshold: agreement.liquidationThreshold,
    });

    const { LTV } = useGetBorrowRate({
        agreement,
        collateralStressTested,
        borrowAmount: assetAmount,
        prices: prices || '0',
        collateralMarketValue,
    });

    const getPriceValue = (amount: string) => {
        let formattedAmount = amount;

        if (amount.includes('≈')) {
            formattedAmount = amount.replace('≈', '').trim();
        }

        const price = Number(formattedAmount) * Number(priceData?.price);

        return price < 0.01 && price > 0 ? price.toPrecision(2) : price.toFixed(2);
    };

    const supplyCollateralValue = useMemo(() => {
        const value = formatWithMaxPrecision(collateralMarketValue ?? '0');

        if (selectedAssets.length > 1) {
            return `~${value}`;
        }

        return value;
    }, [collateralMarketValue, assetAmount]);

    const assetAmountValue = useMemo(() => {
        const value = formatWithMaxPrecision(assetAmount ?? '0');

        if (value === '≈0') {
            return assetAmount;
        }

        return value;
    }, [assetAmount]);

    const borrowAmountValue = useMemo(() => {
        return formatWithMaxPrecision(assetAmount ?? '0');
    }, [assetAmount]);

    return (
        <>
            <p className='text-md font-medium text-content-primary my-3'>Details:</p>
            <div className='flex flex-col p-5 border border-border-opaque rounded'>
                <div className='flex justify-between mb-5'>
                    <div className='flex items-center'>
                        <TooltipWrapper text='Safety of your collateral against borrowed assets.' />
                        <p className='m-0 text-md font-medium text-content-fourth ml-2'>
                            Risk Factor
                        </p>
                    </div>

                    <p
                        className={cn('m-0 text-md font-semibold', {
                            ['text-background-negative']: isHigh,
                            ['text-background-warning']: isWarning,
                            ['text-background-positive']: isLow,
                        })}
                    >
                        {formatWithCommas(Number(riskFactor).toFixed(8))}
                    </p>
                </div>
                <div className='flex justify-between mb-5'>
                    <p className='m-0 text-md font-medium text-content-fourth'>Supply Collateral</p>
                    <div style={{ display: 'flex' }}>
                        <div className='flex items-center ml-2 font-semibold text-[14px] '>
                            {formatWithCommas(supplyCollateralValue)} {tokenSymbol}
                        </div>
                        {pricesLoading ? (
                            <SkeletonInfo />
                        ) : (
                            <div className='flex items-center bg-content-lightAccent text-content-accent font-semibold px-2 py-1 ml-3 rounded text-[10px]'>
                                ${prices ? formatWithCommas(prices) : 'N/A'}
                            </div>
                        )}
                    </div>
                </div>
                <div className='flex justify-between mb-5'>
                    <p className='m-0 text-md font-medium text-content-fourth'>Borrow</p>
                    <div style={{ display: 'flex' }}>
                        <p className='m-0 text-md font-semibold'>
                            {formatWithCommas(borrowAmountValue)} {tokenSymbol}
                        </p>
                        {loading || !priceData?.price ? (
                            <SkeletonInfo />
                        ) : (
                            <div className='flex items-center bg-content-lightAccent text-content-accent font-semibold px-2 py-1 ml-3 rounded text-[10px]'>
                                ${formatWithCommas(getPriceValue(assetAmountValue))}
                            </div>
                        )}
                    </div>
                </div>
                <div className='flex justify-between mb-5'>
                    <p className='m-0 text-md font-medium text-content-fourth'>LTV</p>
                    <p className='m-0 text-md font-semibold'>{LTV}%</p>
                </div>
                <div className='flex justify-between'>
                    <p className='m-0 text-md font-medium text-content-fourth'>Liquidation Fee</p>
                    <p className='m-0 text-md font-semibold'>{agreement?.liquidationFee}%</p>
                </div>
            </div>
        </>
    );
};

export default Details;
